import {
  Calendar,
  CurrencyCircleDollar,
  Syringe,
  ChatText,
  Heart,
  Star,
} from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type {
  CustomerDetailLabels,
  CustomerTimelineEvent,
  CustomerTimelineEventKind,
} from "./types";
import { formatMoney, formatTimeShort } from "./utils";

interface Props {
  event: CustomerTimelineEvent;
  labels: CustomerDetailLabels;
  index?: number;
}

const kindIcon: Record<CustomerTimelineEventKind, Icon> = {
  appointment: Calendar,
  payment: CurrencyCircleDollar,
  vaccination: Syringe,
  note: ChatText,
  review: Heart,
};

const kindTone: Record<CustomerTimelineEventKind, string> = {
  appointment: "bg-biz-info-subtle text-biz-info",
  payment: "bg-biz-success-subtle text-biz-success",
  vaccination: "bg-biz-primary-subtle text-biz-primary",
  note: "bg-biz-muted text-biz-foreground-muted",
  review: "bg-biz-accent-subtle text-biz-accent",
};

export function TimelineRow({ event, labels, index = 0 }: Props) {
  const IconCmp = kindIcon[event.kind];
  const time = formatTimeShort(event.occurredAt);

  return (
    <li
      className="relative flex gap-3 motion-safe:animate-biz-fade-in"
      style={{ animationDelay: `${Math.min(index * 30, 240)}ms` }}
    >
      {/* Rail line */}
      <div className="relative flex flex-col items-center">
        <div
          className={cn(
            "h-9 w-9 rounded-full flex items-center justify-center shrink-0 ring-4 ring-biz-bg",
            kindTone[event.kind],
          )}
          aria-hidden
        >
          <IconCmp size={16} weight="regular" />
        </div>
        <div className="flex-1 w-px bg-biz-border mt-1" />
      </div>

      <div className="flex-1 pb-5 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-biz-foreground-subtle">
                {labels.timeline.types[event.kind]}
              </span>
              {event.status && (
                <span className="text-[11px] text-biz-foreground-subtle font-medium">
                  · {event.status}
                </span>
              )}
            </div>
            <div className="mt-0.5 text-sm font-semibold text-biz-foreground">{event.title}</div>
            {(event.description || event.petName) && (
              <div className="mt-0.5 text-sm text-biz-foreground-muted">
                {event.petName && <span className="font-medium">{event.petName}</span>}
                {event.petName && event.description ? " · " : ""}
                {event.description}
              </div>
            )}
            {event.kind === "review" && typeof event.rating === "number" && (
              <div className="mt-1 flex items-center gap-0.5" aria-label={`${event.rating}/5`}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    size={14}
                    weight={i < (event.rating ?? 0) ? "fill" : "regular"}
                    className={cn(
                      i < (event.rating ?? 0) ? "text-biz-accent" : "text-biz-foreground-subtle",
                    )}
                  />
                ))}
              </div>
            )}
          </div>
          <div className="text-right shrink-0">
            {typeof event.amount === "number" && (
              <div className="text-sm font-bold font-mono-num text-biz-foreground">
                {formatMoney(event.amount, event.currency ?? "TRY")}
              </div>
            )}
            <div className="text-xs text-biz-foreground-subtle font-mono-num">{time}</div>
          </div>
        </div>
      </div>
    </li>
  );
}
